import { useEffect, useRef, useState } from 'react';
import Markdown from 'react-markdown';
import type { SupportChatMessage, SupportChatSource } from '@heybesafe/shared';
import { supportChat } from '../api/client';
import { HbsButton } from './hbs/Button';

/**
 * In-app "how do I use Hey Be Safe?" assistant. Advisory only — it answers
 * product-usage questions grounded in the /support articles and never touches
 * the safe. The conversation lives in component state for this tab only; it is
 * never persisted to localStorage or the server.
 */

interface Turn extends SupportChatMessage {
  sources?: SupportChatSource[];
}

/**
 * Renders an assistant answer as markdown. The model returns markdown
 * (headings, bold, lists, inline links), so we render it to real elements
 * rather than showing the raw syntax. react-markdown builds React nodes —
 * no dangerouslySetInnerHTML, so no HTML-injection surface. Links open in a
 * new tab; styling is applied to the generated descendants via Tailwind
 * arbitrary-variant selectors so the bubble stays compact.
 */
function MarkdownAnswer({ content }: { content: string }): JSX.Element {
  return (
    <div
      className="text-[13px] leading-snug [&_p]:my-2 [&_p:first-child]:mt-0 [&_p:last-child]:mb-0 [&_strong]:font-semibold [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-0.5 [&_h1]:font-semibold [&_h2]:font-semibold [&_h3]:font-semibold [&_h1]:mt-2 [&_h2]:mt-2 [&_h3]:mt-2 [&_code]:rounded [&_code]:bg-hbs-surface [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-[12px] [&_a]:text-hbs-primary [&_a]:underline [&_a]:break-words"
    >
      <Markdown
        components={{
          a: ({ node: _node, ...props }) => (
            <a {...props} target="_blank" rel="noopener noreferrer" />
          ),
        }}
      >
        {content}
      </Markdown>
    </div>
  );
}

const MAX_TURNS = 10;
const SUPPORT_URL = 'https://heybesafe.com/support';
const FALLBACK: Turn = {
  role: 'assistant',
  content: "Sorry — I couldn't answer that just now. You can find help articles and contact support below.",
  sources: [{ title: 'Hey Be Safe Support', url: SUPPORT_URL }],
};

export function SupportChat(): JSX.Element {
  const [open, setOpen] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [turns, loading, open]);

  async function send(): Promise<void> {
    const q = input.trim();
    if (!q || loading) return;
    const next: Turn[] = [...turns, { role: 'user', content: q }];
    setTurns(next);
    setInput('');
    setLoading(true);
    try {
      const history: SupportChatMessage[] = next
        .slice(-MAX_TURNS)
        .map(({ role, content }) => ({ role, content }));
      const res = await supportChat({ messages: history });
      setTurns(
        res.ok
          ? [...next, { role: 'assistant', content: res.data.answer, sources: res.data.sources }]
          : [...next, FALLBACK],
      );
    } catch {
      setTurns([...next, FALLBACK]);
    } finally {
      setLoading(false);
    }
  }

  if (!open) {
    return (
      <button
        type="button"
        aria-label="Open the help assistant"
        id="support-chat-open-button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-40 h-12 px-4 flex items-center gap-2 rounded-full bg-hbs-primary text-hbs-on-primary shadow-hbs-lg hover:bg-hbs-primary-hover"
      >
        <span aria-hidden className="text-[18px] font-bold">?</span>
        <span className="text-[13px] font-semibold">Help</span>
      </button>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-40 flex h-[min(560px,80vh)] w-[380px] max-w-[calc(100vw-2.5rem)] flex-col rounded-hbs-xl border border-hbs-border bg-hbs-surface shadow-hbs-lg">
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-hbs-border px-4 py-3">
        <div className="flex-1">
          <p className="font-display text-[15px] tracking-hbs-tight text-hbs-text">Help</p>
          <p className="text-[11px] text-hbs-text-mute">Ask how to use Hey Be Safe</p>
        </div>
        <button
          type="button"
          aria-label="Close the help assistant"
            id="support-chat-close-button"
            onClick={() => setOpen(false)}
          className="p-1 rounded-hbs-sm text-hbs-text-mute hover:text-hbs-text hover:bg-hbs-surface-alt"
        >
          ×
        </button>
      </div>

      {/* Messages */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-3">
        {turns.length === 0 && (
          <p className="text-[13px] text-hbs-text-mute">
            Hi! Ask me how to do something in Hey Be Safe — like importing passwords, sharing with family, or setting up recovery. I can't see your safe, so I can't look up your specific logins.
          </p>
        )}
        {turns.map((t, i) => (
          <div key={i} className={t.role === 'user' ? 'self-end max-w-[85%]' : 'self-start max-w-[90%]'}>
            <div
              className={`rounded-hbs-lg px-3 py-2 text-[13px] ${
                t.role === 'user'
                  ? 'bg-hbs-primary text-hbs-on-primary whitespace-pre-wrap'
                  : 'bg-hbs-surface-alt text-hbs-text'
              }`}
            >
              {t.role === 'user' ? t.content : <MarkdownAnswer content={t.content} />}
            </div>
            {t.sources && t.sources.length > 0 && (
              <div className="mt-1 flex flex-col gap-0.5">
                {t.sources.map((s) => (
                  <a
                    key={s.url}
                    href={s.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[11.5px] text-hbs-primary hover:underline"
                  >
                    {s.title}
                  </a>
                ))}
              </div>
            )}
          </div>
        ))}
        {loading && <p className="self-start text-[12px] text-hbs-text-mute">Thinking…</p>}
      </div>

      {/* Composer */}
      <form
        className="flex items-center gap-2 border-t border-hbs-border p-3"
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
      >
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask a question…"
          aria-label="Ask a question"
          className="flex-1 h-10 px-3 bg-hbs-surface border border-hbs-border rounded-hbs-sm text-[13.5px] text-hbs-text placeholder:text-hbs-text-mute focus:outline-none focus-within:border-hbs-primary"
        />
        <HbsButton id="support-chat-send-button" type="submit" loading={loading} disabled={!input.trim()}>
          Send
        </HbsButton>
      </form>
    </div>
  );
}
